import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import type { BrokerWriteGuard } from '@/features/brokers/utils/broker-status.util';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useCapitalGlobalCoveragesCatalog } from '@/features/quotes/hooks/capital-global/use-capital-global-coverages-catalog.hook';
import { useCapitalGlobalBusinessSegmentLabel } from '@/features/quotes/hooks/capital-global/use-capital-global-business-segment-label.hook';
import { useInitiateProposal } from '@/features/quotes/hooks/common/use-initiate-proposal.hook';
import { useGenerateProposalPdf } from '@/features/quotes/hooks/homeowner/use-generate-proposal-pdf.hook';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import { stepCalculationFormSchema } from '@/features/quotes/schemas/capital-global/step-calculation.schema';
import {
  isTerminalQuoteStatus,
  PortalStatus,
  TERMINAL_QUOTE_STATUS_LABELS,
} from '@/shared/enums/portal-status.enum';
import { useNavigation } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { downloadBlob } from '@/shared/utils';
import type { CalculationViewData } from '../step-calculation.types';
import { mapProposalDetailsToCalculation } from '../utils/map-proposal-details-to-calculation.util';
import { CAPITAL_GLOBAL_UNDER_ANALYSIS_PLACEHOLDER_INSTALLMENTS } from '../capital-global-under-analysis-placeholders';
import { resolveStipulantSegmentCode } from '../utils/resolve-stipulant-segment-code.util';

type StepCalculationFormApi = {
  state: {
    values: Record<string, never>;
  };
};

type Params = {
  form: unknown;
  onBack: () => void;
};

type Result = {
  calculationData: CalculationViewData;
  isLoading: boolean;
  isUnderAnalysis: boolean;
  isClosed: boolean;
  isTerminalStatus: boolean;
  showBlurredPremiums: boolean;
  isUnderAnalysisModalOpen: boolean;
  closeUnderAnalysisModal: () => void;
  handleEditClick: () => void;
  canEditQuote: boolean;
  handleGeneratePdfClick: () => Promise<void>;
  handleExitClick: () => void;
  handleCreateProposalClick: () => Promise<void>;
  isCreateProposalPending: boolean;
  isGeneratePdfPending: boolean;
  /** Corretora inativa: trava "Editar" (cotação) e "Criar Proposta". */
  quoteWriteGuard: BrokerWriteGuard;
  proposalWriteGuard: BrokerWriteGuard;
  /** Corretora inativa: trava as demais ações da cotação (ex.: "Gerar PDF"). */
  quoteActionGuard: BrokerWriteGuard;
};

const EMPTY_CALCULATION: CalculationViewData = {
  installments: [],
  insuranceDataCard: { title: 'Dados do Seguro', items: [] },
  stipulantDataCard: { title: 'Dados do Estipulante', items: [] },
  calculationDataCard: { title: 'Dados do Cálculo', items: [] },
  calculationDataByStipulant: [],
  stipulantAccordions: [],
  commercialization: { brokeragePercent: '0,00 %', agencingPercent: '0,00 %' },
};

export function useStepCalculationViewModel({
  form,
  onBack,
}: Params): Result {
  const formApi = form as StepCalculationFormApi;
  const { policyId, id } = useQuoteParams();
  const params = useParams({ strict: false });
  const urlPolicyId =
    typeof params.id === 'string' ? params.id.trim() : '';
  const resolvedPolicyId = (policyId || id || urlPolicyId).trim();
  const { push, replace } = useNavigation();
  const initiateProposalMutation = useInitiateProposal();
  const generatePdfMutation = useGenerateProposalPdf();
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const proposalWriteGuard = useSelectedBrokerWriteGuard('proposal');
  const quoteActionGuard = useSelectedBrokerWriteGuard('quote', 'other');

  const [isUnderAnalysisModalOpen, setIsUnderAnalysisModalOpen] = useState(false);
  const hasShownUnderAnalysisModalRef = useRef(false);

  const { data: proposalDetails, isLoading: isLoadingDetails } =
    useProposalDetails(resolvedPolicyId || undefined);

  const isUnderAnalysis =
    Number(proposalDetails?.statusPortal) === PortalStatus.QuoteUnderAnalysis;

  // Cotação encerrada (ex.: recusada na subscrição, status 2) não pode seguir
  // pra proposta nem gerar PDF.
  const isClosed =
    Number(proposalDetails?.statusPortal) === PortalStatus.QuoteClosed;

  // Encerrada/expirada/excluída: nenhum documento novo. Mesma lista que a Api
  // recusa com 409, então o botão fica desabilitado em vez de só falhar no toast.
  const isTerminalStatus = isTerminalQuoteStatus(proposalDetails?.statusPortal);
  const terminalStatusLabel =
    TERMINAL_QUOTE_STATUS_LABELS[Number(proposalDetails?.statusPortal)] ??
    'encerrada';

  const canEditQuote =
    Number(proposalDetails?.statusPortal) === PortalStatus.QuoteInProgress;

  useEffect(() => {
    if (
      !hasShownUnderAnalysisModalRef.current &&
      isUnderAnalysis
    ) {
      hasShownUnderAnalysisModalRef.current = true;
      setIsUnderAnalysisModalOpen(true);
    }
  }, [isUnderAnalysis]);

  const closeUnderAnalysisModal = useCallback(() => {
    setIsUnderAnalysisModalOpen(false);
  }, []);

  const {
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    isLoading: isLoadingCatalog,
  } = useCapitalGlobalCoveragesCatalog();

  const segmentCode = resolveStipulantSegmentCode(proposalDetails);
  const { label: segmentLabel, isLoading: isLoadingSegmentLabel } =
    useCapitalGlobalBusinessSegmentLabel(segmentCode);

  const isLoading =
    isLoadingDetails || isLoadingCatalog || isLoadingSegmentLabel;

  const baseCalculationData: CalculationViewData = proposalDetails
    ? mapProposalDetailsToCalculation(proposalDetails, {
        coverageCatalog,
        assistanceCatalog,
        assistanceFuneralOptions,
        capitalizationOptions,
        segmentLabel,
      })
    : EMPTY_CALCULATION;

  const calculationData = useMemo((): CalculationViewData => {
    if (!isUnderAnalysis) {
      return baseCalculationData;
    }

    return {
      ...baseCalculationData,
      installments:
        baseCalculationData.installments.length > 0
          ? baseCalculationData.installments
          : CAPITAL_GLOBAL_UNDER_ANALYSIS_PLACEHOLDER_INSTALLMENTS,
    };
  }, [baseCalculationData, isUnderAnalysis]);

  const showBlurredPremiums = isUnderAnalysis;

  const handleEditClick = useCallback(() => {
    onBack();
  }, [onBack]);

  const handleGeneratePdfClick = useCallback(async () => {
    if (isUnderAnalysis) {
      toaster.create({
        type: 'warning',
        title: 'Cotação em análise',
        description:
          'A cotação está em análise da subscrição e não pode gerar PDF até a conclusão.',
      });
      return;
    }

    if (isTerminalStatus) {
      toaster.create({
        type: 'warning',
        title: `Cotação ${terminalStatusLabel}`,
        description: `A cotação está ${terminalStatusLabel} e não pode gerar PDF.`,
      });
      return;
    }

    if (!resolvedPolicyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar o PDF',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      return;
    }

    const resolvedProductCode = proposalDetails?.productCode;
    if (!resolvedProductCode) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar o PDF',
        description: 'Código do produto não encontrado.',
      });
      return;
    }

    try {
      const { url, key } = await generatePdfMutation.mutateAsync({
        policyId: resolvedPolicyId,
        productCode: resolvedProductCode,
        stage: 'quote',
      });
      const filename = key.split('/').pop() || `cotacao-${resolvedPolicyId}.pdf`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Falha ao baixar o PDF');
      const blob = await response.blob();
      downloadBlob(blob, filename);
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao gerar PDF',
        description:
          error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    resolvedPolicyId,
    proposalDetails?.productCode,
    generatePdfMutation,
    isUnderAnalysis,
    isTerminalStatus,
    terminalStatusLabel,
  ]);

  const handleExitClick = useCallback(() => {
    push('/quotes');
  }, [push]);

  const handleCreateProposalClick = useCallback(async () => {
    if (isUnderAnalysis) {
      toaster.create({
        type: 'warning',
        title: 'Cotação em análise',
        description:
          'A cotação está em análise da subscrição e não pode gerar proposta até a conclusão.',
      });
      return;
    }

    if (isClosed) {
      toaster.create({
        type: 'warning',
        title: 'Cotação encerrada',
        description:
          'A cotação foi encerrada (recusada na subscrição) e não pode seguir para proposta.',
      });
      return;
    }

    const parsed = stepCalculationFormSchema.safeParse(formApi.state.values);
    if (!parsed.success) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível criar a proposta',
        description: 'Revise os dados do cálculo e tente novamente.',
      });
      return;
    }

    if (!resolvedPolicyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível criar a proposta',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      return;
    }

    try {
      const data = await initiateProposalMutation.mutateAsync({
        policyId: resolvedPolicyId,
      });

      toaster.create({
        type: 'success',
        title: 'Proposta criada com sucesso',
        description: data.proposalNo
          ? `Nº da proposta: ${data.proposalNo}`
          : undefined,
      });

      const proposalPolicyId = data.policyId || resolvedPolicyId;
      if (proposalPolicyId) {
        replace('/proposals/$id/edit', { id: proposalPolicyId });
      }
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível criar a proposta',
        description:
          error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    formApi.state.values,
    isUnderAnalysis,
    isClosed,
    resolvedPolicyId,
    initiateProposalMutation,
    replace,
  ]);

  return {
    calculationData,
    isLoading,
    isUnderAnalysis,
    isClosed,
    isTerminalStatus,
    showBlurredPremiums,
    isUnderAnalysisModalOpen,
    closeUnderAnalysisModal,
    handleEditClick,
    canEditQuote,
    handleGeneratePdfClick,
    handleExitClick,
    handleCreateProposalClick,
    isCreateProposalPending: initiateProposalMutation.isPending,
    isGeneratePdfPending: generatePdfMutation.isPending,
    quoteWriteGuard,
    proposalWriteGuard,
    quoteActionGuard,
  };
}
